// Pure text/tile models for /finance/loan (rendered by pages/finance-loan.js):
// the summary tiles, the "model vs statement" note, loan / contribution-plan
// cards and the history list. Inputs are loans.js rows + a loan-engine.js run.

import { formatMoney, formatPercent } from './money.js';
import { shortDate } from './income-view.js';
import { ME, personName } from './loans.js';

/**
 * How far a statement may differ from the worked-out figure before we warn:
 * this share of it, or `DRIFT_NOTE_MIN_CENTS`, whichever is bigger.
 */
export const DRIFT_NOTE_SHARE = 0.1;
export const DRIFT_NOTE_MIN_CENTS = 1_000_00;

export const ENTRY_LABELS = Object.freeze({
  rate: 'Interest rate change',
  extra: 'Extra repayment',
  redraw: 'Redraw',
  balance: 'Loan statement balance',
  'offset-deposit': 'Offset deposit',
  'offset-withdrawal': 'Offset withdrawal',
  'offset-balance': 'Offset statement balance',
});

export const ENTRY_ICONS = Object.freeze({
  rate: 'fa-percent',
  extra: 'fa-circle-down',
  redraw: 'fa-circle-up',
  balance: 'fa-file-invoice',
  'offset-deposit': 'fa-piggy-bank',
  'offset-withdrawal': 'fa-money-bill-transfer',
  'offset-balance': 'fa-file-invoice-dollar',
});

const PERIOD_TEXT = Object.freeze({
  weekly: 'each week',
  fortnightly: 'each fortnight',
  monthly: 'each month',
});

/** "6.04%" from a stored % a year. */
export function rateText(ratePct) {
  return formatPercent(ratePct / 100, { decimals: 2 });
}

/** The entry type of an offset row ('offset-deposit' / …). */
export const offsetEntryType = (entry) => `offset-${entry.kind}`;

/** The summary tiles for a simulation (`[]` when there's nothing to show). */
export function loanTiles(sim) {
  const now = sim?.now;
  if (!now) return [];
  return [
    { key: 'owing', label: 'Owing today', cents: now.balanceCents, hint: 'Across your loans' },
    {
      key: 'offset',
      label: 'Offset account',
      cents: now.offsetCents,
      hint: 'Reduces the interest on the variable portions',
    },
    {
      key: 'net',
      label: 'Owing less offset',
      cents: Math.max(0, now.balanceCents - now.offsetCents),
      hint: 'What interest is charged on',
    },
    {
      key: 'interest-paid',
      label: 'Interest so far',
      cents: sim.interestToDateCents,
      hint: 'Since settlement',
    },
    {
      key: 'interest-left',
      label: 'Interest still to come',
      cents: sim.interestRemainingCents,
      hint: 'At today’s rates and repayments',
    },
    {
      key: 'payoff',
      label: 'Paid off',
      value: sim.payoffDate ? shortDate(sim.payoffDate) : 'Not within 40 years',
      hint: 'At today’s rates and repayments',
    },
    {
      key: 'covered',
      label: 'Offset covers the loan',
      value: sim.coveredDate ? shortDate(sim.coveredDate) : 'Not yet projected',
      hint: 'When the offset first reaches what you owe',
    },
  ];
}

/**
 * Whether a statement balance is far enough from the model to suggest a missing
 * entry: off by 10% of the worked-out figure or AU$1,000, whichever is bigger.
 */
export function significantDrift({ diffCents, modelledCents }) {
  const threshold = Math.max(Math.abs(modelledCents) * DRIFT_NOTE_SHARE, DRIFT_NOTE_MIN_CENTS);
  return Math.abs(diffCents) >= threshold;
}

/**
 * The warning on the most recent statement balance that was well off the model
 * (`significantDrift`): `{ kind: 'warning', message }`, or null.
 */
export function driftNote(sim) {
  if (!sim) return null;
  const last = [
    ...sim.anchors.map((a) => ({ ...a, what: 'loan balance' })),
    ...sim.offsetAnchors.filter((a) => !a.opening).map((a) => ({ ...a, what: 'offset balance' })),
  ]
    .filter(significantDrift)
    .sort((a, b) => a.date.localeCompare(b.date))
    .at(-1);
  if (!last) return null;
  const more = last.diffCents > 0 ? 'more' : 'less';
  return {
    kind: 'warning',
    message: `Your ${last.what} on ${shortDate(last.date)} was ${formatMoney(
      last.actualCents,
    )} — ${formatMoney(Math.abs(last.diffCents))} ${more} than worked out from your entries (${formatMoney(
      last.modelledCents,
    )}). Everything after uses the statement figure. Check for a missing rate change, extra repayment, redraw or offset deposit or withdrawal before that date.`,
  };
}

/** The latest entry of `type` on or before `date` for a portion (or the whole loan). */
function latestFor(events, loanRef, portionId, type, date) {
  return events.findLast(
    (e) =>
      e.loanRef === loanRef &&
      e.type === type &&
      e.date <= date &&
      (e.portionId === null || e.portionId === portionId),
  );
}

/** A loan card's text: `{ title, badges, details }`. */
export function describeLoan(loan, { events = [], loans = [], today }) {
  const closed = loan.closedOn && loan.closedOn <= today;
  const from = loan.refinancedFrom ? loans.find((l) => l.itemId === loan.refinancedFrom) : null;
  const portions = loan.portions.map((p) => {
    const rate = latestFor(events, loan.itemId, p.id, 'rate', today);
    const parts = [
      `${formatMoney(p.principal.amount)} over ${p.termYears} years`,
      rate ? rateText(rate.rate) : 'no rate yet',
      p.offset ? 'offset applies' : null,
    ].filter(Boolean);
    return {
      key: `portion-${p.id}`,
      label: `${p.name} (${p.type === 'fixed' ? 'fixed' : 'variable'})`,
      value: parts.join(' · '),
    };
  });
  return {
    title: loan.name,
    badges: [closed ? 'Closed' : 'Current', ...(from ? ['Refinanced'] : [])],
    details: [
      { key: 'lender', label: 'Lender', value: loan.lender || null },
      { key: 'settled', label: 'Settled', value: shortDate(loan.settlementDate) },
      { key: 'from', label: 'Refinanced from', value: from ? from.name : null },
      { key: 'closed', label: 'Closed', value: loan.closedOn ? shortDate(loan.closedOn) : null },
      {
        key: 'day',
        label: 'Repayments',
        value: `On day ${loan.repaymentDay} of each month`,
      },
      ...portions,
      { key: 'notes', label: 'Notes', value: loan.notes || null },
    ].filter((d) => d.value !== null),
  };
}

/** "AU$1,200.00 each payday" for one person's share. */
function shareText(share) {
  return `${formatMoney(share.amount.amount)} ${share.period ? PERIOD_TEXT[share.period] : 'each payday'}`;
}

/** A contribution plan card's text: `{ title, badges, details }` (me first). */
export function describeContributionPlan(plan, people, { current = false } = {}) {
  const ids = Object.keys(plan.amounts).sort((a, b) =>
    a === ME ? -1 : b === ME ? 1 : personName(people, a).localeCompare(personName(people, b)),
  );
  const paused = !ids.length;
  return {
    title: `From ${shortDate(plan.from)}`,
    badges: [...(current ? ['Current'] : []), ...(paused ? ['Paused'] : [])],
    details: paused
      ? [{ key: 'paused', label: 'Contributions', value: 'None — paused' }]
      : ids.map((id) => ({
          key: `person-${id}`,
          label: personName(people, id),
          value: shareText(plan.amounts[id]),
        })),
  };
}

/**
 * The chart's data from a run's points: `{ labels, titles, owing, offset,
 * todayIndex }` — values in dollars; `todayIndex` = the last point on or
 * before `today` (later points are the projection).
 */
export function loanChartData(sim, today) {
  const points = sim?.points ?? [];
  return {
    labels: points.map((p) => p.date.slice(0, 7)),
    titles: points.map((p) => shortDate(p.date)),
    owing: points.map((p) => p.balanceCents / 100),
    offset: points.map((p) => p.offsetCents / 100),
    todayIndex: points.findLastIndex((p) => p.date <= today),
  };
}

/** A history entry's title, e.g. "Interest rate 6.04%", "Extra repayment AU$10,000.00". */
function entryTitle(type, row) {
  if (type === 'rate') return `Interest rate ${rateText(row.rate)}`;
  return `${ENTRY_LABELS[type]} ${formatMoney(row.amount.amount)}`;
}

/**
 * The history list, newest first: `[{ itemId, date, type, icon, title, meta,
 * row, source: 'event'|'offset' }]` — `meta` = date · the loan (with several)
 * · the portion · "from the offset" · the note.
 */
export function historyItems({ loans = [], events = [], offset = [] }) {
  const several = loans.length > 1;
  const items = [
    ...events.map((e) => {
      const loan = loans.find((l) => l.itemId === e.loanRef);
      const portion = e.portionId ? loan?.portions.find((p) => p.id === e.portionId) : null;
      return {
        itemId: e.itemId,
        date: e.date,
        type: e.type,
        source: 'event',
        row: e,
        meta: [
          shortDate(e.date),
          several ? loan?.name : null,
          portion ? `${portion.name} portion` : null,
          e.type === 'extra' ? (e.fromOffset ? 'From the offset' : 'Paid in directly') : null,
          e.note || null,
        ],
      };
    }),
    ...offset.map((o) => ({
      itemId: o.itemId,
      date: o.date,
      type: offsetEntryType(o),
      source: 'offset',
      row: o,
      meta: [shortDate(o.date), o.note || null],
    })),
  ];
  return items
    .map((i) => ({
      ...i,
      icon: ENTRY_ICONS[i.type],
      title: entryTitle(i.type, i.row),
      meta: i.meta.filter(Boolean),
    }))
    .sort((a, b) => b.date.localeCompare(a.date));
}
